import React, { useEffect, useMemo, useState } from 'react';
import { localizationAPI } from '../services/api';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/LocalizationPage.module.css';

const EMPTY_ENTRY = { key: '', value: '' };

const LocalizationPage = () => {
  const {
    t,
    languages,
    defaultLanguage,
    translations,
    currentLanguage,
    setCurrentLanguage,
    setLocalizationPayload,
    refreshLocalization,
  } = useLocalization();

  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [entries, setEntries] = useState([EMPTY_ENTRY]);
  const [newLanguageCode, setNewLanguageCode] = useState('');
  const [newLanguageName, setNewLanguageName] = useState('');
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const allKeys = useMemo(() => {
    const keys = new Set();
    Object.values(translations || {}).forEach((dictionary) => {
      Object.keys(dictionary || {}).forEach((key) => keys.add(key));
    });
    return Array.from(keys).sort();
  }, [translations]);

  useEffect(() => {
    if (!selectedLanguage && languages.length > 0) {
      setSelectedLanguage(languages[0].code);
      return;
    }

    if (selectedLanguage && !languages.some((language) => language.code === selectedLanguage)) {
      setSelectedLanguage(defaultLanguage || 'en');
    }
  }, [defaultLanguage, languages, selectedLanguage]);

  useEffect(() => {
    const languageTranslations = translations[selectedLanguage] || {};

    const mapped = allKeys.map((key) => ({
      key,
      value: languageTranslations[key] || '',
    }));

    if (mapped.length === 0) {
      setEntries([EMPTY_ENTRY]);
    } else {
      setEntries(mapped);
    }
  }, [allKeys, selectedLanguage, translations]);

  const setEntry = (index, patch) => {
    setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, EMPTY_ENTRY]);
  };

  const removeEntry = (index) => {
    setEntries((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const saveTranslations = async () => {
    try {
      setSaving(true);
      const payload = {};
      entries.forEach(({ key, value }) => {
        const normalized = String(key || '').trim();
        if (!normalized) return;
        payload[normalized] = String(value ?? '');
      });

      const response = await localizationAPI.saveTranslations(selectedLanguage, payload);
      setLocalizationPayload(response.data);
      alert(t('localization_save_success', 'Translations saved successfully'));
    } catch (error) {
      alert(error.response?.data?.message || t('localization_save_failed', 'Failed to save language settings'));
    } finally {
      setSaving(false);
    }
  };

  const saveDefaultLanguage = async (code) => {
    try {
      setSaving(true);
      const response = await localizationAPI.setDefaultLanguage(code);
      setLocalizationPayload(response.data);
      alert(t('localization_save_success', 'Translations saved successfully'));
    } catch (error) {
      alert(error.response?.data?.message || t('localization_save_failed', 'Failed to save language settings'));
    } finally {
      setSaving(false);
    }
  };

  const createLanguage = async () => {
    try {
      const code = newLanguageCode.trim().toLowerCase();
      const name = newLanguageName.trim();
      if (!code || !name) {
        alert(`${t('localization_code', 'Code')} & ${t('localization_name', 'Name')} are required`);
        return;
      }

      setCreating(true);
      const response = await localizationAPI.createLanguage({ code, name });
      setLocalizationPayload(response.data);
      setSelectedLanguage(code);
      setNewLanguageCode('');
      setNewLanguageName('');
      alert(t('localization_create_success', 'Language created successfully'));
    } catch (error) {
      alert(error.response?.data?.message || t('localization_create_failed', 'Failed to create language'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('localization_title', 'Language Management')}</h1>
        <p>{t('localization_subtitle', 'Manage app languages and customize translation text in system settings.')}</p>
      </div>

      <section className={styles.card}>
        <div className={styles.inlineRow}>
          <label>{t('localization_system_default', 'System default language')}</label>
          <select
            className={styles.select}
            value={defaultLanguage}
            onChange={(e) => saveDefaultLanguage(e.target.value)}
            disabled={saving}
          >
            {languages.map((language) => (
              <option key={language.code} value={language.code}>{language.name} ({language.code})</option>
            ))}
          </select>
        </div>

        <div className={styles.inlineRow}>
          <label>{t('localization_selected_language', 'Selected language')}</label>
          <select
            className={styles.select}
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            {languages.map((language) => (
              <option key={language.code} value={language.code}>{language.name} ({language.code})</option>
            ))}
          </select>

          <label>{t('common_language', 'Language')} (UI)</label>
          <select
            className={styles.select}
            value={currentLanguage}
            onChange={(e) => setCurrentLanguage(e.target.value)}
          >
            {languages.filter((language) => language.isActive).map((language) => (
              <option key={language.code} value={language.code}>{language.name}</option>
            ))}
          </select>

          <button type="button" className={styles.secondaryBtn} onClick={refreshLocalization}>
            Refresh
          </button>
        </div>

        <div className={styles.addLanguageRow}>
          <h3>{t('localization_add_language', 'Add Language')}</h3>
          <div className={styles.inlineRow}>
            <input
              className={styles.input}
              placeholder={t('localization_code', 'Code')}
              value={newLanguageCode}
              onChange={(e) => setNewLanguageCode(e.target.value)}
            />
            <input
              className={styles.input}
              placeholder={t('localization_name', 'Name')}
              value={newLanguageName}
              onChange={(e) => setNewLanguageName(e.target.value)}
            />
            <button type="button" className={styles.primaryBtn} onClick={createLanguage} disabled={creating}>
              {creating ? t('common_saving', 'Saving...') : t('common_add', 'Add')}
            </button>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>{selectedLanguage}</h3>
          <button type="button" className={styles.primaryBtn} onClick={saveTranslations} disabled={saving}>
            {saving ? t('common_saving', 'Saving...') : t('common_save', 'Save')}
          </button>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('localization_key', 'Key')}</th>
                <th>{t('localization_text', 'Text')}</th>
                <th>{t('common_cancel', 'Cancel')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={3} className={styles.empty}>{t('localization_no_entries', 'No translation entries yet')}</td>
                </tr>
              ) : (
                entries.map((entry, index) => (
                  <tr key={`${entry.key}_${index}`}>
                    <td>
                      <input
                        className={styles.input}
                        value={entry.key}
                        onChange={(e) => setEntry(index, { key: e.target.value })}
                        placeholder="translation_key"
                      />
                    </td>
                    <td>
                      <input
                        className={styles.input}
                        value={entry.value}
                        onChange={(e) => setEntry(index, { value: e.target.value })}
                      />
                    </td>
                    <td>
                      <button type="button" className={styles.removeBtn} onClick={() => removeEntry(index)}>
                        {t('localization_remove_entry', 'Remove')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <button type="button" className={styles.secondaryBtn} onClick={addEntry}>
          {t('localization_add_entry', 'Add translation entry')}
        </button>
      </section>
    </div>
  );
};

export default LocalizationPage;
